import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight } from 'lucide-react';

import { DiscoverIcon, OrderIcon, ReadyIcon } from './StepIcons';
import './style/WelcomeModal.css';

/* ═══════════════════════════════════════════════════════════════
   WELCOME MODAL — the one-screen pitch for FoodFinder.

   Shown on a visitor's first Home view, then again seven days
   after they last dismissed it. Tapping the primary CTA is taken
   as "message received" and retires the modal permanently on that
   device; skipping or closing only restarts the seven-day clock.

   Everything the modal needs to decide that lives in one
   localStorage entry (see WELCOME_KEY). Home.js owns the `open`
   prop; `shouldShowWelcome()` below is the gate it calls.

   Step icons: previously three Lottie players. Swapped for static
   SVGs (see StepIcons.jsx) — no missing-asset failure mode, no
   per-device animation cost, and no risk of a broken player shell
   showing a stray inner circle if the animation JSON is absent.
═══════════════════════════════════════════════════════════════ */

export const WELCOME_KEY = 'ff_welcome_v1';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const readState = () => {
  try {
    const raw = localStorage.getItem(WELCOME_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    /* private mode, disabled storage, or corrupt JSON — treat as
       first visit rather than blocking the modal outright. */
    return null;
  }
};

const writeState = (next) => {
  try {
    localStorage.setItem(WELCOME_KEY, JSON.stringify(next));
  } catch {
    /* Storage unavailable. The modal simply shows again next visit. */
  }
};

/** True when the intro is due: never seen, or last dismissed 7+ days
 *  ago and not already retired by a CTA tap. */
export const shouldShowWelcome = () => {
  const state = readState();
  if (!state) return true;
  if (state.completed) return false;
  if (typeof state.lastSeen !== 'number') return true;
  return Date.now() - state.lastSeen >= SEVEN_DAYS_MS;
};

/* ── The three beats of the story, in order ── */
const STEPS = [
  {
    n: '01',
    Icon: DiscoverIcon,
    title: 'Search your mood, not a menu',
    body: 'Vibe, cuisine, ambience, occasion — find the right table near you.',
  },
  {
    n: '02',
    Icon: OrderIcon,
    title: 'Order before you get there',
    body: 'One tap on the way. The kitchen starts the moment you confirm.',
  },
  {
    n: '03',
    Icon: ReadyIcon,
    title: 'Sit down to food, not a wait',
    body: 'You arrive, it arrives. No menu queue, no kitchen queue.',
  },
];

/* Focusable children of the dialog, for the tab trap. */
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const WelcomeModal = ({ open, onClose, onPrimary }) => {
  const dialogRef = useRef(null);
  const primaryRef = useRef(null);
  const lastFocusedRef = useRef(null);
  /* Drives the entrance transition — mounted first, shown one frame
     later, so the browser has a "from" state to animate out of. */
  const [entered, setEntered] = useState(false);

  const dismiss = useCallback((completed) => {
    writeState({ lastSeen: Date.now(), completed });
    onClose();
  }, [onClose]);

  const handlePrimary = useCallback(() => {
    /* CTA tap means the pitch landed — don't show it again. */
    writeState({ lastSeen: Date.now(), completed: true });
    onClose();
    onPrimary?.();
  }, [onClose, onPrimary]);

  /* Body scroll lock, entrance frame, focus move + restore. */
  useEffect(() => {
    if (!open) return undefined;

    lastFocusedRef.current = document.activeElement;
    const { overflow, paddingRight } = document.body.style;
    /* Compensate for the scrollbar so the page behind doesn't shift. */
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    const raf = requestAnimationFrame(() => setEntered(true));
    const focusTimer = setTimeout(() => primaryRef.current?.focus(), 220);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(focusTimer);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      setEntered(false);
      if (lastFocusedRef.current instanceof HTMLElement) lastFocusedRef.current.focus();
    };
  }, [open]);

  /* Escape to close, Tab cycles inside the dialog. */
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        dismiss(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const nodes = dialogRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes?.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, dismiss]);

  if (!open) return null;

  return createPortal(
    <div
      className={`wm-overlay ${entered ? 'wm-overlay--in' : ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(false); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`wm-card ${entered ? 'wm-card--in' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wm-title"
        aria-describedby="wm-steps"
      >
        <button className="wm-close" onClick={() => dismiss(false)} aria-label="Close">
          <X size={17} />
        </button>

        {/* ── Header: brand mark + the promise, on the same dark
            gradient the Home hero uses ── */}
        <header className="wm-head">
          <div className="wm-brand">
            <img
              src="/production_logo/RestroPilots_Monogram_Logomark.svg"
              alt=""
              className="wm-brand-mark"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="wm-brand-text">FoodFinder <em>by RestroPilots</em></span>
          </div>
          <h2 id="wm-title" className="wm-title">
            Never wait at the<br />table again
          </h2>
        </header>

        {/* ── The flow, three beats on one screen ── */}
        <ol className="wm-steps" id="wm-steps">
          {STEPS.map((step, i) => (
            <li className="wm-step" key={step.n} style={{ '--wm-delay': `${120 + i * 90}ms` }}>
              <div className="wm-step-rail" aria-hidden="true">
                <span className="wm-step-disc">
                  <step.Icon className="wm-step-icon" />
                </span>
              </div>
              <div className="wm-step-copy">
                <span className="wm-step-n">{step.n}</span>
                <h3 className="wm-step-title">{step.title}</h3>
                <p className="wm-step-body">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* ── Actions ── */}
        <footer className="wm-foot">
          <button ref={primaryRef} className="wm-cta" onClick={handlePrimary}>
            <span>Find food near me</span>
            <ArrowRight size={17} />
          </button>
          <button className="wm-skip" onClick={() => dismiss(false)}>Maybe later</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default WelcomeModal;